import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationsAPI } from '../../services/api';
import '../../App.css';

const TYPE_ICONS  = { inbound_email: '📬', user_registration: '👤' };
const TYPE_LABELS = { inbound_email: 'Support Email', user_registration: 'New Registration' };

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d    = new Date(iso);
    const now  = new Date();
    const diff = Math.floor((now - d) / 60000);
    if (diff < 1)   return 'Just now';
    if (diff < 60)  return `${diff}m ago`;
    const hrs = Math.floor(diff / 60);
    if (hrs  < 24)  return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7)   return `${days}d ago`;
    return d.toLocaleDateString();
  } catch { return iso; }
}

export default function Notifications() {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState('');
  const [selected, setSelected]           = useState(null);
  const [unreadCount, setUnreadCount]     = useState(0);

  // Reply state
  const [replySubject, setReplySubject] = useState('');
  const [replyBody, setReplyBody]       = useState('');
  const [replying, setReplying]         = useState(false);
  const [replyError, setReplyError]     = useState('');
  const [replySuccess, setReplySuccess] = useState('');
  const [showReply, setShowReply]       = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await notificationsAPI.getAll();
      if (res.success) {
        setNotifications(res.data.notifications || []);
        setUnreadCount(res.data.unreadCount || 0);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load notifications.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNotification = async (n) => {
    setSelected(n);
    setShowReply(false);
    setReplyError('');
    setReplySuccess('');
    if (!n.read) {
      try {
        await notificationsAPI.markRead(n.id);
        setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (_) {}
    }
  };

  const deleteNotification = async (id) => {
    if (!window.confirm('Delete this notification? This cannot be undone.')) return;
    try {
      await notificationsAPI.remove(id);
      setNotifications(prev => prev.filter(x => x.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch {
      alert('Failed to delete notification.');
    }
  };

  const dismissAll = async () => {
    if (!window.confirm('Clear all notifications? This will permanently delete them all.')) return;
    try {
      await notificationsAPI.dismissAll();
      setNotifications([]);
      setUnreadCount(0);
      setSelected(null);
    } catch {
      alert('Failed to clear notifications.');
    }
  };

  // ── Reply ────────────────────────────────────────────────────────────────────
  const openReply = () => {
    const orig = selected?.data?.subject || '';
    setReplySubject(orig.toLowerCase().startsWith('re:') ? orig : `Re: ${orig}`);
    setReplyBody('');
    setReplyError('');
    setReplySuccess('');
    setShowReply(true);
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!replyBody.trim()) { setReplyError('Please write a reply.'); return; }
    setReplying(true);
    setReplyError('');
    try {
      await notificationsAPI.replyToEmail(selected.id, { subject: replySubject, body: replyBody });
      setReplySuccess('Reply sent successfully.');
      setShowReply(false);
      setSelected(prev => ({ ...prev, repliedAt: new Date().toISOString() }));
    } catch (err) {
      setReplyError(err.response?.data?.error || 'Failed to send reply.');
    } finally {
      setReplying(false);
    }
  };

  // ── Detail pane ──────────────────────────────────────────────────────────────
  if (selected) {
    const data    = selected.data || {};
    const isEmail = selected.type === 'inbound_email';

    return (
      <div className="container">
        <div className="page-header">
          <button className="btn btn-secondary" onClick={() => setSelected(null)}>← Back</button>
          <h1>{TYPE_ICONS[selected.type] || '🔔'} {TYPE_LABELS[selected.type] || 'Notification'}</h1>
        </div>

        <div className="detail-card">
          <h2>{selected.title}</h2>

          {isEmail && (
            <table className="meta-table">
              <tbody>
                <tr><td className="meta-label">From</td><td>{data.from || '—'}</td></tr>
                <tr><td className="meta-label">To</td><td>{(data.to || []).join(', ') || '—'}</td></tr>
                <tr><td className="meta-label">Subject</td><td>{data.subject || '—'}</td></tr>
                <tr><td className="meta-label">Received</td><td>{formatDate(selected.createdAt)}</td></tr>
                {selected.repliedAt && (
                  <tr><td className="meta-label">Replied</td><td style={{color:'#4caf50'}}>✓ {formatDate(selected.repliedAt)}</td></tr>
                )}
              </tbody>
            </table>
          )}

          {selected.type === 'user_registration' && (
            <table className="meta-table">
              <tbody>
                <tr><td className="meta-label">Username</td><td>{data.username || '—'}</td></tr>
                <tr><td className="meta-label">Email</td><td>{data.email || '—'}</td></tr>
                <tr><td className="meta-label">Received</td><td>{formatDate(selected.createdAt)}</td></tr>
              </tbody>
            </table>
          )}

          {/* Body */}
          <div className="email-body-box">
            {isEmail && data.html
              ? <div dangerouslySetInnerHTML={{ __html: data.html }} />
              : <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{data.text || selected.body || '(no content)'}</pre>
            }
          </div>

          {/* Action buttons */}
          <div className="detail-actions">
            {isEmail && (
              <button className="btn btn-primary" onClick={openReply}>↩️ Reply</button>
            )}
            {selected.type === 'user_registration' && data.userId && (
              <button className="btn btn-secondary" onClick={() => navigate('/admin/users')}>
                👤 Go to User Management
              </button>
            )}
            <button className="btn btn-danger" onClick={() => deleteNotification(selected.id)}>
              🗑 Delete
            </button>
          </div>

          {replySuccess && <p className="success-msg">{replySuccess}</p>}
        </div>

        {/* Reply form */}
        {showReply && (
          <div className="reply-card">
            <h3>↩️ Reply to {data.from}</h3>
            <form onSubmit={sendReply}>
              <div className="form-group">
                <label>Subject</label>
                <input
                  className="form-control"
                  value={replySubject}
                  onChange={e => setReplySubject(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Message</label>
                <textarea
                  className="form-control"
                  rows={8}
                  value={replyBody}
                  onChange={e => setReplyBody(e.target.value)}
                  placeholder="Write your reply…"
                  required
                />
              </div>
              {replyError && <p className="error">{replyError}</p>}
              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={replying}>
                  {replying ? 'Sending…' : '📤 Send Reply'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowReply(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <NotifStyles />
      </div>
    );
  }

  // ── List view ────────────────────────────────────────────────────────────────
  return (
    <div className="container">
      <div className="page-header">
        <h1>🔔 Notifications{unreadCount > 0 ? ` (${unreadCount} unread)` : ''}</h1>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={load}>↻ Refresh</button>
          {notifications.length > 0 && (
            <button className="btn btn-danger" onClick={dismissAll}>Clear All</button>
          )}
          <button className="btn btn-primary" onClick={() => navigate('/admin/compose-email')}>
            ✉️ Compose Email
          </button>
        </div>
      </div>

      {error   && <p className="error">{error}</p>}
      {loading && <p>Loading notifications…</p>}

      {!loading && notifications.length === 0 && (
        <div className="empty-state">
          <p>No notifications yet. New user registrations and inbound support emails will appear here.</p>
        </div>
      )}

      {!loading && notifications.length > 0 && (
        <div className="notif-list">
          {notifications.map(n => (
            <div
              key={n.id}
              className={`notif-row ${!n.read ? 'unread' : ''}`}
              onClick={() => openNotification(n)}
            >
              <span className="notif-icon">{TYPE_ICONS[n.type] || '🔔'}</span>
              <div className="notif-info">
                <span className="notif-title">{n.title}</span>
                <span className="notif-subtitle">{n.body}</span>
              </div>
              <div className="notif-meta">
                <span className="notif-date">{formatDate(n.createdAt)}</span>
                {!n.read && <span className="badge-unread">New</span>}
              </div>
              <button
                className="btn-icon"
                title="Delete"
                onClick={e => { e.stopPropagation(); deleteNotification(n.id); }}
              >✕</button>
            </div>
          ))}
        </div>
      )}

      <NotifStyles />
    </div>
  );
}

function NotifStyles() {
  return (
    <style jsx="true">{`
      .page-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 1rem;
        margin-bottom: 1.5rem;
      }
      .page-header h1 { margin: 0; }
      .header-actions { display: flex; gap: 0.75rem; flex-wrap: wrap; }

      /* list */
      .notif-list { display: flex; flex-direction: column; gap: 0.5rem; }
      .notif-row {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 0.9rem 1rem;
        background: #fff;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        cursor: pointer;
        transition: box-shadow 0.15s;
      }
      .notif-row:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.12); }
      .notif-row.unread { border-left: 4px solid #007bff; background: #f0f7ff; }
      .notif-icon { font-size: 1.4rem; flex-shrink: 0; }
      .notif-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 0.2rem; }
      .notif-title { font-weight: 600; color: #222; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .notif-row.unread .notif-title { font-weight: 700; }
      .notif-subtitle { font-size: 0.85rem; color: #666; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .notif-meta { display: flex; flex-direction: column; align-items: flex-end; gap: 0.3rem; flex-shrink: 0; }
      .notif-date { font-size: 0.8rem; color: #999; white-space: nowrap; }
      .badge-unread { background: #007bff; color: #fff; font-size: 0.7rem; padding: 2px 7px; border-radius: 10px; font-weight: 700; }
      .btn-icon { background: none; border: none; cursor: pointer; color: #999; font-size: 1rem; padding: 4px 6px; border-radius: 4px; }
      .btn-icon:hover { color: #f44336; background: #ffeaea; }

      /* detail */
      .detail-card {
        background: #fff;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        padding: 1.5rem;
        margin-bottom: 1.5rem;
      }
      .detail-card h2 { margin-top: 0; }
      .meta-table { border-collapse: collapse; margin-bottom: 1.25rem; width: 100%; }
      .meta-table td { padding: 0.35rem 0.75rem 0.35rem 0; font-size: 0.9rem; vertical-align: top; }
      .meta-label { color: #777; font-weight: 600; white-space: nowrap; width: 90px; }
      .email-body-box {
        border: 1px solid #e0e0e0;
        border-radius: 6px;
        padding: 1rem;
        background: #fafafa;
        overflow: auto;
        max-height: 500px;
        margin-bottom: 1.25rem;
      }
      .detail-actions { display: flex; gap: 0.75rem; flex-wrap: wrap; margin-bottom: 0.75rem; }

      /* reply */
      .reply-card {
        background: #fff;
        border: 1px solid #007bff;
        border-radius: 8px;
        padding: 1.5rem;
        margin-bottom: 1.5rem;
      }
      .reply-card h3 { margin-top: 0; }
      .form-group { margin-bottom: 1rem; }
      .form-group label { display: block; font-weight: 600; margin-bottom: 0.3rem; color: #555; }
      .form-control { width: 100%; padding: 0.6rem 0.75rem; border: 1px solid #ccc; border-radius: 4px; font-size: 0.95rem; box-sizing: border-box; }
      .form-control:focus { outline: none; border-color: #007bff; box-shadow: 0 0 0 2px rgba(0,123,255,0.15); }
      textarea.form-control { resize: vertical; font-family: inherit; }
      .form-actions { display: flex; gap: 0.75rem; }

      /* empty state */
      .empty-state {
        background: #f9f9f9;
        border: 1px dashed #ccc;
        border-radius: 8px;
        padding: 2.5rem;
        text-align: center;
        color: #777;
      }

      /* misc */
      .success-msg { color: #4caf50; font-weight: 600; }
      .btn { padding: 0.55rem 1.1rem; border: none; border-radius: 4px; cursor: pointer; font-size: 0.9rem; transition: all 0.2s; }
      .btn-primary { background: #007bff; color: #fff; }
      .btn-primary:hover { background: #0056b3; }
      .btn-secondary { background: #e0e0e0; color: #333; }
      .btn-secondary:hover { background: #c8c8c8; }
      .btn-danger { background: #f44336; color: #fff; }
      .btn-danger:hover { background: #c62828; }
      .btn:disabled { opacity: 0.6; cursor: not-allowed; }
    `}</style>
  );
}
